import { useEffect, useRef, useState } from 'react';

import { useAddToast } from '../../../contexts/ToastContext';
import Button from '../../generic/Button/Button';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import PasswordInput from '../../generic/Forms/PasswordInput';
import ForgotPasswordLink from '../components/ForgotPasswordLink';
import UseDifferentAccountButton from '../components/UseDifferentAccountButton';
import { presentRequestError } from '../errorPresentation';
import { useCredentialLogin } from '../hooks/useCredentialLogin';
import { useLoginResponseEffect } from '../hooks/useLoginResponseEffect';
import { STRATEGY_LABEL } from '../strategyLabels';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { Candidate } from '../../../../shared/types/routes/auth/v2';

import styles from '../Login.module.less';

export type CredentialFormProps = {
  /**
   * The org being logged into. `null` (the only org-less value — never undefined) means an org-less
   * login: an existing account with no organization yet, authenticating to resume onboarding; the
   * request then omits `organizationId`.
   */
  org: Candidate | null;
  identifier: string;
  /** Which credential method the form serves; `ldap` shows an LDAP hint. The backend still picks the strategy. */
  credentialKind: 'password' | 'ldap';
  showForgotPassword: boolean;
  next?: string;
  onMfaRequired: (mfaMethod: MfaMethodType) => void;
  onBack: () => void;
};

/**
 * The credential step: the user enters their password for the org they picked. The identifier is shown read-only —
 * changing it means going back to the identifier step. The backend resolves the credential against the org's
 * configured method (local password, or LDAP), so this one form serves every credential org.
 */
const CredentialForm = ({
  org,
  identifier,
  credentialKind,
  showForgotPassword,
  next,
  onMfaRequired,
  onBack,
}: CredentialFormProps) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const credentialLogin = useCredentialLogin();
  const addToast = useAddToast();

  // The identifier field is read-only, so land focus on the password instead.
  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  // On success, hand off to the MFA step or redirect into the app. (The error path stays inline below since it
  // doesn't unmount the form.)
  useLoginResponseEffect(credentialLogin.data, onMfaRequired);

  const handleSubmit = () => {
    if (!password || credentialLogin.isLoading) {
      return;
    }
    setError(null);
    credentialLogin.mutate(
      { identifier, password, ...(org ? { organizationId: org.organizationId } : {}), ...(next ? { next } : {}) },
      {
        onError: requestError => {
          // Bad credentials (400) and an SSO-required org (403) are expected outcomes shown inline; anything else
          // (network failure, 5xx) is unexpected and surfaces as a toast.
          const presented = presentRequestError(requestError, {
            inlineFallback: 'Invalid username or password.',
            toastFallback: 'Something went wrong. Please try again.',
          });
          if (presented.kind === 'inline') {
            setError(presented.message);
            return;
          }
          addToast({ type: 'error', content: presented.message });
        },
      },
    );
  };

  return (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h1 className={styles.heading}>{org ? `Sign in to ${org.organizationName}` : 'Welcome back'}</h1>
      {!org && (
        <p className={styles.subheading}>Sign in to finish setting up your account.</p>
      )}
      {credentialKind === 'ldap' && (
        <p className={styles.subheading} data-test-id="ldap-hint">
          Use your {STRATEGY_LABEL.ldap} directory credentials.
        </p>
      )}
      <div className={styles.fieldGroup}>
        <LabeledTextInput label="Email or username" name="identifier" value={identifier} isReadOnly />
        <PasswordInput ref={passwordRef} label="Password" value={password} onChange={setPassword} isRequired />
        {showForgotPassword && <ForgotPasswordLink identifier={identifier} next={next} />}
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {/* A submit button lets Enter submit the form; the login runs in the form's onSubmit, so onClick is unused. */}
      <Button isFullWidth size="large" isSubmitButton isDisabled={credentialLogin.isLoading} onClick={() => undefined}>
        Sign in
      </Button>
      <UseDifferentAccountButton onClick={onBack} />
    </form>
  );
};

export default CredentialForm;
